import React, { useEffect, useState } from "react";
import { useAuth, adminAxios, errText } from "../AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2, Edit, Loader2, Shield, ExternalLink, Key, UserCog, Building2, Paintbrush, Pencil, Copy, Check } from "lucide-react";

export default function SuperAdminPage() {
    const { user, loading, logout, impersonate } = useAuth();
    const nav = useNavigate();
    const [tenants, setTenants] = useState([]);
    const [busy, setBusy] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [showReset, setShowReset] = useState(null);
    const [copiedSlug, setCopiedSlug] = useState("");

    const copyPublicUrl = async (slug) => {
        const url = `${window.location.origin}/#/?borgo=${encodeURIComponent(slug)}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedSlug(slug);
            setTimeout(() => setCopiedSlug(""), 2000);
        } catch (_) { window.prompt("Copia:", url); }
    };

    const renameSlug = async (t) => {
        const raw = window.prompt(
            `Nuovo slug per "${t.name}"\n\nAttuale: ${t.slug}\n\nATTENZIONE: cambiare lo slug modifica l'URL pubblico del borgo. I vecchi link (bookmark, condivisioni social, QR code) NON funzioneranno più.\n\nUsa solo lettere minuscole, numeri e trattini.`,
            t.slug
        );
        if (!raw || raw === t.slug) return;
        try {
            // Fetch current tenant, patch slug, put back — server sanitizes and validates uniqueness
            const { data: current } = await adminAxios().get(`/admin/tenants/${t.id}`);
            current.slug = raw;
            const { data: updated } = await adminAxios().put(`/admin/tenants/${t.id}`, current);
            window.alert(`Slug aggiornato: ${updated.slug}`);
            await load();
        } catch (e) {
            window.alert(errText(e) || "Errore aggiornamento slug");
        }
    };

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await adminAxios().get("/admin/tenants");
            setTenants(data);
        } finally { setBusy(false); }
    };

    useEffect(() => { if (user?.role === "super_admin") load(); }, [user]);

    if (loading) return <div className="pt-32 min-h-screen text-center text-stone-500">Caricamento…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "super_admin") return <Navigate to="/admin" replace />;

    const del = async (t) => {
        if (!confirm(`Confermi eliminazione del borgo '${t.name}'?\nSaranno eliminati anche immobili/eventi/attrattori/utenti associati.`)) return;
        await adminAxios().delete(`/admin/tenants/${t.id}`);
        load();
    };

    const doImpersonate = async (t) => {
        await impersonate(t.id);
        nav("/admin");
    };

    const doCustomize = async (t) => {
        // Set a pending flag so AdminDashboard picks up the target tab even if
        // the router's <Navigate> guard wipes the query string during role flip.
        sessionStorage.setItem("tada_pending_admin_tab", "personalizza");
        await impersonate(t.id);
        nav("/admin?tab=personalizza");
    };

    return (
        <div className="pt-24 min-h-screen bg-stone-100" data-testid="super-admin-page">
            <div className="bg-gradient-to-br from-indigo-700 to-blue-700 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-wrap justify-between items-center gap-4">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-indigo-200">Super Admin · Trivor</div>
                        <div className="font-serif text-3xl md:text-4xl font-semibold mt-1">Piattaforma Multi-Borgo</div>
                        <div className="text-indigo-100 text-sm mt-1">Ciao <b>{user.username}</b> — gestisci tutti i borghi della piattaforma</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-indigo-700 font-semibold text-sm hover:bg-indigo-50" data-testid="sa-new-tenant">
                            <Plus size={16} /> Nuovo Borgo
                        </button>
                        <button onClick={() => { logout(); nav("/"); }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-white/50 text-white font-semibold text-sm hover:bg-white/10" data-testid="sa-logout">
                            <LogOut size={16} /> Esci
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                {busy && <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-indigo-600" /></div>}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tenants.map((t) => (
                        <div key={t.id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden card-hover" data-testid={`sa-tenant-${t.slug}`}>
                            <div className="aspect-[16/9] bg-gradient-to-br from-emerald-100 to-amber-50 relative">
                                {t.hero?.image_url ? (
                                    <img src={t.hero.image_url} alt={t.name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-emerald-300">
                                        <Building2 size={48} />
                                    </div>
                                )}
                                {t.logo_url && (
                                    <div className="absolute bottom-3 left-3 w-14 h-14 rounded-full bg-white p-1 shadow-lg">
                                        <img src={t.logo_url} alt="logo" className="w-full h-full rounded-full object-cover" />
                                    </div>
                                )}
                                <span className={`absolute top-3 right-3 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${t.status === "active" ? "bg-emerald-500 text-white" : "bg-stone-400 text-white"}`}>
                                    {t.status}
                                </span>
                            </div>
                            <div className="p-5">
                                <div className="font-serif text-xl font-semibold text-stone-900">{t.name}</div>
                                <div className="text-xs font-mono text-stone-500 mt-1 flex items-center gap-2">
                                    slug: <b>{t.slug}</b>
                                    <button
                                        onClick={() => renameSlug(t)}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-widest"
                                        data-testid={`sa-rename-slug-${t.slug}`}
                                        title="Modifica slug URL del borgo"
                                    >
                                        <Pencil size={9} /> Modifica
                                    </button>
                                </div>
                                <div className="text-xs text-stone-500 mt-1 flex items-center gap-1"><UserCog size={12} /> admin: <b>{t.admin_username}</b></div>
                                <div className="mt-4 grid grid-cols-2 gap-2">
                                    <a
                                        href={`/#/?borgo=${t.slug}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-stone-100 text-stone-700 hover:bg-stone-200 text-xs font-semibold"
                                        data-testid={`sa-view-${t.slug}`}
                                    >
                                        <ExternalLink size={12} /> Vista pubblica
                                    </a>
                                    <button
                                        onClick={() => copyPublicUrl(t.slug)}
                                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-emerald-50 text-emerald-800 hover:bg-emerald-100 text-xs font-semibold"
                                        data-testid={`sa-copy-url-${t.slug}`}
                                        title="Copia il link pubblico del borgo (senza login)"
                                    >
                                        {copiedSlug === t.slug ? <><Check size={12} /> Copiato</> : <><Copy size={12} /> Copia link</>}
                                    </button>
                                    <button
                                        onClick={() => doImpersonate(t)}
                                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 text-xs font-semibold"
                                        data-testid={`sa-impersonate-${t.slug}`}
                                    >
                                        <Shield size={12} /> Impersona
                                    </button>
                                    <button
                                        onClick={() => doCustomize(t)}
                                        className="col-span-2 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-semibold"
                                        data-testid={`sa-customize-${t.slug}`}
                                    >
                                        <Paintbrush size={12} /> Personalizza (logo, hero, gallery, contatti)
                                    </button>
                                    <button
                                        onClick={() => setShowReset(t)}
                                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 text-xs font-semibold"
                                        data-testid={`sa-reset-${t.slug}`}
                                    >
                                        <Key size={12} /> Reset PWD
                                    </button>
                                    <button
                                        onClick={() => del(t)}
                                        disabled={t.slug === "tadasuni"}
                                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold"
                                        data-testid={`sa-delete-${t.slug}`}
                                        title={t.slug === "tadasuni" ? "Non puoi eliminare il borgo di default" : "Elimina borgo"}
                                    >
                                        <Trash2 size={12} /> Elimina
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {!busy && tenants.length === 0 && (
                    <div className="text-center py-16 bg-white rounded-2xl border border-stone-200 text-stone-500">
                        Nessun borgo caricato — clicca &quot;Nuovo Borgo&quot; per crearne uno.
                    </div>
                )}
            </div>

            {showNew && <NewTenantModal onClose={() => setShowNew(false)} onCreated={load} />}
            {showReset && <ResetPasswordModal tenant={showReset} onClose={() => setShowReset(null)} />}
        </div>
    );
}

const NewTenantModal = ({ onClose, onCreated }) => {
    const [f, setF] = useState({ slug: "", name: "", admin_username: "", admin_password: "", admin_email: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        try {
            await adminAxios().post("/admin/tenants", f);
            onCreated();
            onClose();
        } catch (e2) {
            setErr(errText(e2, "Errore"));
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="sa-new-modal">
            <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl">
                <div className="px-6 py-4 bg-gradient-to-r from-indigo-700 to-blue-700 text-white flex justify-between items-center">
                    <div className="font-serif text-xl font-semibold">Nuovo Borgo</div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/15 text-xl">×</button>
                </div>
                <form onSubmit={submit} className="p-6 space-y-4">
                    <div>
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">Slug URL *</label>
                        <input required value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="orgosolo" className="input-elegant font-mono" data-testid="sa-new-slug" />
                        <div className="text-xs text-stone-500 mt-1">URL pubblico: <code>/#/?borgo=<b>{f.slug || "slug"}</b></code></div>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">Nome Borgo *</label>
                        <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="VisitOrgosolo" className="input-elegant" data-testid="sa-new-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">Admin Username *</label>
                            <input required value={f.admin_username} onChange={(e) => setF({ ...f, admin_username: e.target.value })} placeholder="orgosolo_admin" className="input-elegant" data-testid="sa-new-username" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">Admin Password *</label>
                            <input required type="text" value={f.admin_password} onChange={(e) => setF({ ...f, admin_password: e.target.value })} placeholder="Password2025$" className="input-elegant font-mono" data-testid="sa-new-password" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">Admin Email</label>
                        <input type="email" value={f.admin_email} onChange={(e) => setF({ ...f, admin_email: e.target.value })} placeholder="info@borgo.it" className="input-elegant" data-testid="sa-new-email" />
                    </div>
                    {err && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="sa-new-error">{err}</div>}
                    <div className="flex justify-end gap-3 pt-3 border-t border-stone-100">
                        <button type="button" onClick={onClose} className="px-5 py-2 rounded-full border border-stone-300 font-semibold text-sm">Annulla</button>
                        <button type="submit" disabled={busy} className="btn-primary px-5 py-2 rounded-full font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="sa-new-submit">
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Crea Borgo
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ResetPasswordModal = ({ tenant, onClose }) => {
    const [pw, setPw] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setMsg("");
        try {
            await adminAxios().post(`/admin/tenants/${tenant.id}/reset-password`, { new_password: pw });
            setMsg("✓ Password aggiornata");
            setTimeout(onClose, 1200);
        } catch (e2) {
            setMsg("✗ " + errText(e2, "Errore"));
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="sa-reset-modal">
            <form onSubmit={submit} className="bg-white w-full max-w-md rounded-2xl overflow-hidden shadow-2xl p-6">
                <div className="font-serif text-xl font-semibold mb-1">Reset Password Admin</div>
                <div className="text-sm text-stone-500 mb-4">Borgo <b>{tenant.name}</b> · admin: <code>{tenant.admin_username}</code></div>
                <input required type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Nuova password" className="input-elegant font-mono" data-testid="sa-reset-pw" autoFocus />
                {msg && <div className={`mt-3 text-sm ${msg.startsWith("✓") ? "text-emerald-700" : "text-red-700"}`} data-testid="sa-reset-msg">{msg}</div>}
                <div className="flex justify-end gap-3 mt-5">
                    <button type="button" onClick={onClose} className="px-5 py-2 rounded-full border border-stone-300 font-semibold text-sm">Annulla</button>
                    <button type="submit" disabled={busy || !pw} className="btn-primary px-5 py-2 rounded-full font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="sa-reset-submit">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />} Aggiorna
                    </button>
                </div>
            </form>
        </div>
    );
};
